(() => {
  if (customElements.get('epelay-review-carousel')) return;

  class EpelayReviewCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.scrollFrame = null;
      this.abortController = null;
      this.dragState = null;
      this.resizeObserver = null;
      this.handleVisibilityChange = () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      };
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cacheElements();
      this.bindEvents();
      this.loadReviews();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    cacheElements() {
      this.loading = this.querySelector('[data-loading]');
      this.error = this.querySelector('[data-error]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.defaultErrorMessage = this.dataset.errorLabel || 'Live reviews are temporarily unavailable.';
      this.empty = this.querySelector('[data-empty]');
      this.carousel = this.querySelector('[data-carousel]');
      this.viewport = this.querySelector('[data-review-viewport]');
      this.track = this.querySelector('[data-review-track]');
      this.template = this.querySelector('[data-review-card-template]');
      this.retryButton = this.querySelector('[data-review-retry]');
      this.previousButton = this.querySelector('[data-review-prev]');
      this.nextButton = this.querySelector('[data-review-next]');
      this.carouselLabel = this.querySelector('[data-carousel-label]');
      this.summary = this.querySelector('[data-review-summary]');
      this.layout = this.querySelector('[data-review-layout]') || this.querySelector('.epelay-reviews__layout');
      this.distribution = this.querySelector('[data-review-distribution]') || this.querySelector('.epelay-reviews__distribution');
      this.averageRating = this.querySelector('[data-average-rating]');
      this.averageStars = this.querySelector('[data-average-stars]');
      this.ratingCount = this.querySelector('[data-rating-count]');
      this.sourceNote = this.querySelector('[data-source-note]');
      this.reviewLimit = Math.max(1, Number.parseInt(this.dataset.reviewLimit, 10) || 8);
      this.sourceUrl = this.safeUrl(this.dataset.sourceUrl, window.location.href);

      const readerConfigs = [
        {
          endpoint: this.getAttribute('data-reader-endpoint'),
          label: this.getAttribute('data-reader-label')
        },
        {
          endpoint: this.getAttribute('data-reader-endpoint-2'),
          label: this.getAttribute('data-reader-label-2')
        },
        {
          endpoint: this.getAttribute('data-reader-endpoint-3'),
          label: this.getAttribute('data-reader-label-3')
        }
      ];
      this.readerSources = readerConfigs
        .map((config) => this.createReaderSource(config.endpoint, config.label))
        .filter(Boolean);
      this.invalidReaderSourceCount = readerConfigs.filter((config) =>
        Boolean((config.endpoint || '').trim()) && !this.createReaderSource(config.endpoint, config.label)
      ).length;
    }

    bindEvents() {
      this.retryButton?.addEventListener('click', () => this.loadReviews());
      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      this.track?.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.syncCarouselState();
        });
      }, { passive: true });

      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.currentIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + 1);
        }
      });

      this.track?.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.track?.addEventListener('pointermove', (event) => this.moveDrag(event));
      this.track?.addEventListener('pointerup', (event) => this.endDrag(event));
      this.track?.addEventListener('pointercancel', (event) => this.endDrag(event));
      this.track?.addEventListener('click', (event) => {
        if (!this.dragState?.didMove) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncCarouselState());
        this.resizeObserver.observe(this);
      }
    }

    async loadReviews() {
      this.abortController?.abort();
      const loadController = new AbortController();
      this.abortController = loadController;
      this.setLoadingState();

      const endpoint = (this.dataset.endpoint || '').trim();
      const endpointUrl = endpoint ? this.buildEndpointUrl(endpoint) : '';

      if (!endpointUrl && !this.readerSources.length) {
        const invalidConfiguration = Boolean(endpoint) || this.invalidReaderSourceCount > 0;
        this.setErrorState(
          invalidConfiguration
            ? 'Enter a valid HTTPS review feed endpoint.'
            : this.dataset.configMessage || 'The verified review feed is not configured yet.',
          false
        );
        return;
      }

      try {
        let payload = null;
        let endpointError = null;

        if (endpointUrl) {
          try {
            const endpointPayload = await this.fetchJsonFeed(endpointUrl);
            if (!this.hasReviewCollection(endpointPayload)) {
              throw new Error('Primary review feed returned no usable reviews');
            }
            payload = endpointPayload;
          } catch (error) {
            endpointError = error;
            payload = null;
          }
        }

        if (!payload && this.readerSources.length) {
          payload = await this.fetchTikTokReaders();
        }

        if (!payload) throw endpointError || new Error('No live review source is available');
        this.renderPayload(payload);
      } catch (error) {
        if (this.abortController !== loadController || !this.isConnected) return;
        this.setErrorState();
        if (error.name !== 'AbortError') console.warn('[Epelay reviews] Live feed unavailable.', error);
      }
    }

    async fetchJsonFeed(endpointUrl) {
      const response = await this.fetchWithTimeout(endpointUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      }, 10000);

      if (!response.ok) throw new Error(`Review feed returned ${response.status}`);
      return response.json();
    }

    async fetchTikTokReaders() {
      const results = await Promise.allSettled(
        this.readerSources.map((source) => this.fetchTikTokReader(source))
      );
      const payloads = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      if (!payloads.length) throw new Error('TikTok review sources are unavailable');
      return this.mergeTikTokPayloads(payloads);
    }

    async fetchTikTokReader(source) {
      const response = await this.fetchWithTimeout(source.readerUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
          'X-Cache-Tolerance': '300',
          'X-Return-Format': 'html'
        },
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      }, 15000);

      if (!response.ok) throw new Error(`TikTok reader returned ${response.status}`);
      const html = await response.text();
      if (html.length > 2000000) throw new Error('TikTok reader response is too large');
      return this.payloadFromTikTokHtml(html, source);
    }

    async fetchWithTimeout(url, options, timeoutMs) {
      const requestController = new AbortController();
      const abortRequest = () => requestController.abort();
      const timeout = window.setTimeout(abortRequest, timeoutMs);
      const parentSignal = this.abortController.signal;
      parentSignal.addEventListener('abort', abortRequest, { once: true });

      try {
        return await fetch(url, { ...options, signal: requestController.signal });
      } finally {
        window.clearTimeout(timeout);
        parentSignal.removeEventListener('abort', abortRequest);
      }
    }

    payloadFromTikTokHtml(html, source) {
      const match = html.match(/<script[^>]*id=["']__MODERN_ROUTER_DATA__["'][^>]*>([\s\S]*?)<\/script>/i);
      if (!match?.[1]) throw new Error('TikTok page data is missing');

      this.reviewSearchNodes = 0;
      const reviewInfo = this.findTikTokReviewInfo(JSON.parse(match[1]), source.productId);
      if (!reviewInfo) throw new Error('TikTok review data is missing');

      const ratingSummary = this.isObject(reviewInfo.review_ratings) ? reviewInfo.review_ratings : {};
      const distribution = this.isObject(ratingSummary.rating_result) ? ratingSummary.rating_result : {};
      const totalReviews = this.firstNumber(ratingSummary.review_count, reviewInfo.total_reviews) || 0;
      const averageRating = this.firstNumber(ratingSummary.overall_score);
      const seenReviewIds = new Set();
      const reviews = (Array.isArray(reviewInfo.product_reviews) ? reviewInfo.product_reviews : [])
        .filter((review) => {
          if (!this.isObject(review)) return false;
          if (String(review.product_id || '') !== source.productId) return false;
          if (review.is_verified_purchase !== true || review.is_incentivized_review !== false) return false;

          const id = typeof review.review_id === 'string' ? review.review_id.trim() : '';
          const body = typeof review.review_text === 'string' ? review.review_text.trim() : '';
          const rating = Number(review.review_rating);
          if (!id || !body || !Number.isInteger(rating) || rating < 1 || rating > 5 || seenReviewIds.has(id)) return false;
          seenReviewIds.add(id);
          return true;
        })
        .map((review) => ({
          id: String(review.review_id).trim().slice(0, 64),
          reviewer_name: typeof review.reviewer_name === 'string' ? review.reviewer_name.trim().slice(0, 120) : '',
          location: this.countryName(review.review_country),
          avatar_url: typeof review.reviewer_avatar_url === 'string' ? review.reviewer_avatar_url.slice(0, 2048) : '',
          rating: Number(review.review_rating),
          product_label: [
            source.label,
            typeof review.sku_specification === 'string' ? review.sku_specification.trim().slice(0, 150) : ''
          ].filter(Boolean).join(' · ').slice(0, 180),
          body: typeof review.review_text === 'string' ? review.review_text.trim().slice(0, 2000) : '',
          date: this.epochToIso(review.review_time),
          verified: true,
          source_label: 'TikTok Shop',
          source_url: source.sourceUrl
        }))
        .filter((review) => review.id && review.body);

      return {
        source: 'TikTok Shop',
        source_url: source.sourceUrl,
        fetched_at: new Date().toISOString(),
        summary: {
          average_rating: Number.isFinite(averageRating) && averageRating > 0 && averageRating <= 5
            ? averageRating
            : null,
          total_reviews: totalReviews,
          rating_count_label: `Based on ${this.formatNumber(totalReviews)} TikTok Shop reviews`,
          rating_distribution: distribution,
          updated_label: 'Loaded from TikTok Shop'
        },
        reviews
      };
    }

    mergeTikTokPayloads(payloads) {
      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const seenReviewIds = new Set();
      const reviews = [];
      let totalReviews = 0;
      let weightedRating = 0;
      let ratedReviews = 0;

      payloads.forEach((payload) => {
        const count = this.firstNumber(payload.summary?.total_reviews) || 0;
        const average = this.firstNumber(payload.summary?.average_rating);
        totalReviews += count;
        if (count > 0 && average > 0 && average <= 5) {
          weightedRating += average * count;
          ratedReviews += count;
        }
        for (let rating = 1; rating <= 5; rating += 1) {
          ratingDistribution[rating] += this.firstNumber(payload.summary?.rating_distribution?.[rating]) || 0;
        }

        (payload.reviews || []).forEach((review) => {
          if (!review.id || seenReviewIds.has(review.id)) return;
          seenReviewIds.add(review.id);
          reviews.push(review);
        });
      });

      return {
        source: 'TikTok Shop',
        source_url: this.sourceUrl,
        fetched_at: new Date().toISOString(),
        summary: {
          average_rating: ratedReviews === totalReviews && totalReviews > 0
            ? weightedRating / totalReviews
            : null,
          total_reviews: totalReviews,
          rating_count_label: `Based on ${this.formatNumber(totalReviews)} TikTok Shop reviews`,
          rating_distribution: ratingDistribution,
          updated_label: `Loaded from ${payloads.length} TikTok Shop product pages`
        },
        reviews
      };
    }

    findTikTokReviewInfo(value, productId, depth = 0) {
      this.reviewSearchNodes = (this.reviewSearchNodes || 0) + 1;
      if (this.reviewSearchNodes > 10000 || depth > 12 || value === null || typeof value !== 'object') return null;

      if (Array.isArray(value)) {
        for (const child of value) {
          const found = this.findTikTokReviewInfo(child, productId, depth + 1);
          if (found) return found;
        }
        return null;
      }

      if (this.isObject(value.review_info)) {
        const reviews = value.review_info.product_reviews;
        const ratings = value.review_info.review_ratings;
        const reviewCount = this.firstNumber(ratings?.review_count, value.review_info.total_reviews);
        const hasMatchingReview = Array.isArray(reviews) && reviews.some((review) =>
          this.isObject(review) && String(review.product_id || '') === productId
        );
        const isValidEmptyFeed = Array.isArray(reviews) &&
          this.isObject(ratings) &&
          reviews.length === 0 &&
          reviewCount === 0;

        if (this.isObject(ratings) && (hasMatchingReview || isValidEmptyFeed)) {
          return value.review_info;
        }
      }

      for (const child of Object.values(value)) {
        const found = this.findTikTokReviewInfo(child, productId, depth + 1);
        if (found) return found;
      }
      return null;
    }

    normalizePayload(payload) {
      const payloadRoot = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
      const root = payloadRoot || {};
      const summary = root.summary || root.rating_summary || root.ratings || root;
      const sourceReviews = this.findReviewArray(root);

      const reviews = sourceReviews
        .map((review) => this.normalizeReview(review))
        .filter((review) => review.body && review.verified && review.rating >= 1 && review.rating <= 5)
        .slice(0, this.reviewLimit);

      const averageRating = this.firstNumber(
        summary.average_rating,
        summary.averageRating,
        summary.rating
      );
      const totalReviews = this.firstNumber(
        summary.total_reviews,
        summary.totalReviews,
        summary.review_count,
        summary.reviewCount,
        summary.count
      );
      const ratingCountLabel = this.firstText(
        summary.rating_count_label,
        summary.ratingCountLabel
      );
      const distributionSource = summary.rating_distribution || summary.ratingDistribution || summary.distribution || {};
      const validAverageRating = Number.isFinite(averageRating) && averageRating > 0 && averageRating <= 5
        ? averageRating
        : null;

      return {
        averageRating: validAverageRating,
        totalReviews,
        ratingCountLabel: ratingCountLabel || (Number.isFinite(totalReviews) ? `Based on ${this.formatNumber(totalReviews)} verified purchases` : ''),
        distribution: this.normalizeDistribution(distributionSource, totalReviews),
        updatedLabel: this.firstText(summary.updated_label, summary.updatedLabel, summary.updated_at, summary.updatedAt, root.updated_at) || '',
        reviews
      };
    }

    findReviewArray(root) {
      if (Array.isArray(root)) return root;
      if (Array.isArray(root.reviews)) return root.reviews;
      if (Array.isArray(root.items)) return root.items;
      if (Array.isArray(root.results)) return root.results;
      if (Array.isArray(root.data)) return root.data;
      return [];
    }

    hasReviewCollection(payload) {
      const payloadRoot = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
      const root = payloadRoot || {};
      if (Array.isArray(root)) return true;
      return ['reviews', 'items', 'results', 'data'].some((key) => Array.isArray(root[key]));
    }

    normalizeReview(review = {}) {
      const author = typeof review.author === 'object' ? review.author : {};
      const user = typeof review.user === 'object' ? review.user : {};
      const name = this.firstText(review.reviewer_name, review.reviewerName, review.display_name, review.displayName, author.name, user.name, typeof review.author === 'string' ? review.author : '');
      const body = this.firstText(review.body, review.review_body, review.reviewBody, review.content, review.text, review.comment);
      const rawRating = this.firstNumber(review.rating, review.stars, review.score);
      const rating = Number.isInteger(rawRating) && rawRating >= 1 && rawRating <= 5 ? rawRating : null;
      const verifiedValue = review.verified ?? review.verified_purchase ?? review.verifiedPurchase ?? review.is_verified_purchase;

      return {
        id: this.firstText(review.id, review.review_id, review.reviewId, review.source_id),
        name: name || 'Verified buyer',
        initial: this.firstText(review.reviewer_initial, review.reviewerInitial, review.initial) || this.firstVisibleCharacter(name) || 'E',
        location: this.firstText(review.location, review.country, author.location, user.location),
        avatarUrl: this.firstText(review.avatar_url, review.avatarUrl, author.avatar_url, author.avatar, user.avatar_url, user.avatar),
        rating,
        productLabel: this.firstText(review.product_label, review.productLabel, review.variant_name, review.variantName, review.sku_name, review.skuName),
        body,
        date: this.formatDate(this.firstText(review.date, review.review_date, review.reviewDate, review.created_at, review.createdAt)),
        verified: verifiedValue === true || verifiedValue === 1 || verifiedValue === 'true' || verifiedValue === 'verified',
        sourceLabel: this.firstText(review.source_label, review.sourceLabel, review.source) || 'TikTok Shop',
        sourceUrl: this.safeUrl(this.firstText(review.source_url, review.sourceUrl, review.url), this.sourceUrl)
      };
    }

    normalizeDistribution(source, totalReviews) {
      const values = {};
      let explicitValueType = '';
      if (Array.isArray(source)) {
        source.forEach((item) => {
          const key = Math.round(this.firstNumber(item.stars, item.rating, item.score) || 0);
          const percentage = this.firstNumber(item.percentage, item.percent);
          const count = this.firstNumber(item.count);
          if (Number.isFinite(percentage)) {
            values[key] = percentage;
            if (explicitValueType !== 'count') explicitValueType = 'percentage';
          } else if (Number.isFinite(count)) {
            values[key] = count;
            if (explicitValueType !== 'percentage') explicitValueType = 'count';
          } else {
            values[key] = this.firstNumber(item.value) || 0;
          }
        });
      } else {
        for (let rating = 1; rating <= 5; rating += 1) {
          values[rating] = this.firstNumber(
            source[rating],
            source[String(rating)],
            source[`${rating}_star`],
            source[`${rating}Star`]
          ) || 0;
        }
      }

      const sum = Object.values(values).reduce((total, value) => total + value, 0);
      const validTotal = Number.isFinite(totalReviews) && totalReviews > 0 ? totalReviews : null;
      const closeToTotal = validTotal !== null && Math.abs(sum - validTotal) <= Math.max(1, validTotal * 0.02);
      const valuesAreCounts = explicitValueType === 'count' ||
        (explicitValueType !== 'percentage' && (
          closeToTotal ||
          sum > 105 ||
          Object.values(values).some((value) => value > 100)
        ));
      const denominator = validTotal || sum;

      for (let rating = 1; rating <= 5; rating += 1) {
        const raw = values[rating] || 0;
        values[rating] = this.clamp(valuesAreCounts && denominator ? (raw / denominator) * 100 : raw, 0, 100);
      }
      return values;
    }

    renderPayload(payload) {
      const normalized = this.normalizePayload(payload);

      if (!normalized.reviews.length) {
        this.setEmptyState();
        return;
      }

      this.renderSummary(normalized);

      const fragment = document.createDocumentFragment();
      normalized.reviews.forEach((review) => fragment.append(this.createReviewCard(review)));
      this.track.replaceChildren(fragment);
      this.cards = Array.from(this.track.children);
      this.currentIndex = 0;
      this.track.scrollLeft = 0;

      this.loading.hidden = true;
      this.empty.hidden = true;
      this.carousel.hidden = false;
      this.summary.setAttribute('aria-busy', 'false');

      this.error.hidden = true;
      this.error.classList.remove('is-inline');

      requestAnimationFrame(() => {
        this.syncCarouselState();
        this.startAutoplay();
      });
    }

    renderSummary(data) {
      const rating = Number.isFinite(data.averageRating) && data.averageRating > 0 && data.averageRating <= 5
        ? data.averageRating
        : null;
      const showSummary = rating !== null && Number.isFinite(data.totalReviews) && data.totalReviews > 0;
      this.summary.hidden = !showSummary;
      this.layout.classList.toggle('is-without-summary', !showSummary);
      if (!showSummary) return;

      this.averageRating.textContent = rating.toFixed(1);
      this.renderAverageStars(rating);
      this.ratingCount.textContent = data.ratingCountLabel || '';
      this.sourceNote.textContent = data.updatedLabel || '';

      const hasDistribution = Object.values(data.distribution).some((percentage) => percentage > 0);
      this.distribution.hidden = !hasDistribution;

      this.querySelectorAll('[data-rating-row]').forEach((row) => {
        const ratingKey = row.dataset.ratingRow;
        const percentage = Math.round(data.distribution[ratingKey] || 0);
        row.querySelector('[data-rating-bar]')?.style.setProperty('--epelay-rating-width', `${percentage}%`);
        const label = row.querySelector('[data-rating-percent]');
        if (label) label.textContent = `${percentage}%`;
      });
    }

    averageStarFills(rating) {
      return Array.from({ length: 5 }, (_item, index) => this.clamp(rating - index, 0, 1));
    }

    renderAverageStars(rating) {
      const stars = this.averageStarFills(rating).map((fill) => {
        const star = document.createElement('i');
        star.textContent = '★';
        star.setAttribute('aria-hidden', 'true');
        if (fill >= 1) {
          star.classList.add('is-full');
        } else if (fill > 0) {
          star.classList.add('is-partial');
          star.style.setProperty('--epelay-star-fill', `${Math.round(fill * 100)}%`);
        } else {
          star.classList.add('is-empty');
        }
        return star;
      });
      this.averageStars.replaceChildren(...stars);
      this.averageStars.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
    }

    createReviewCard(review) {
      const card = this.template.content.firstElementChild.cloneNode(true);
      if (review.id) card.dataset.reviewSourceId = review.id;
      card.setAttribute('aria-label', `${review.rating} out of 5 stars from ${review.name}`);

      const name = card.querySelector('[data-review-name]');
      const location = card.querySelector('[data-review-location]');
      const image = card.querySelector('img.epelay-review-card__avatar');
      const fallback = card.querySelector('.epelay-review-card__avatar--fallback');
      const stars = card.querySelector('[data-review-stars]');
      const verified = card.querySelector('[data-review-verified]');
      const product = card.querySelector('[data-review-product]');
      const body = card.querySelector('[data-review-body]');
      const source = card.querySelector('[data-review-source]');
      const date = card.querySelector('[data-review-date]');

      name.textContent = review.name;
      location.textContent = review.location;
      location.hidden = !review.location;
      fallback.textContent = review.initial;

      const avatarUrl = this.safeUrl(review.avatarUrl, '');
      if (avatarUrl) {
        image.src = avatarUrl;
        image.alt = `${review.name} avatar`;
        image.referrerPolicy = 'no-referrer';
        image.hidden = false;
        fallback.hidden = true;
      }

      for (let index = 1; index <= 5; index += 1) {
        const star = document.createElement('i');
        star.textContent = '★';
        star.setAttribute('aria-hidden', 'true');
        if (index > review.rating) star.classList.add('is-empty');
        stars.append(star);
      }
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      verified.hidden = !review.verified;

      product.textContent = review.productLabel;
      product.hidden = !review.productLabel;
      body.textContent = `“${review.body}”`;
      date.textContent = review.date;
      date.hidden = !review.date;
      source.textContent = review.sourceLabel;
      source.href = review.sourceUrl;
      source.hidden = !review.sourceUrl;

      return card;
    }

    setLoadingState() {
      this.stopAutoplay();
      this.summary.hidden = true;
      this.layout.classList.add('is-without-summary');
      this.retryButton.hidden = false;
      this.errorMessage.textContent = this.defaultErrorMessage;
      this.loading.hidden = false;
      this.error.hidden = true;
      this.empty.hidden = true;
      this.carousel.hidden = true;
      this.summary.setAttribute('aria-busy', 'true');
    }

    setErrorState(message = '', retryable = true) {
      this.summary.hidden = true;
      this.layout.classList.add('is-without-summary');
      this.loading.hidden = true;
      this.empty.hidden = true;
      this.carousel.hidden = true;
      this.error.hidden = false;
      this.error.classList.remove('is-inline');
      this.errorMessage.textContent = message || this.defaultErrorMessage;
      this.retryButton.hidden = !retryable;
      this.summary.setAttribute('aria-busy', 'false');
    }

    setEmptyState() {
      this.stopAutoplay();
      this.summary.hidden = true;
      this.layout.classList.add('is-without-summary');
      this.empty.textContent = this.dataset.emptyLabel || 'No verified reviews are available yet.';
      this.loading.hidden = true;
      this.error.hidden = true;
      this.carousel.hidden = true;
      this.empty.hidden = false;
      this.summary.setAttribute('aria-busy', 'false');
    }

    getCardStep() {
      if (!this.cards?.length) return 0;
      if (this.cards.length > 1) return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
      return this.cards[0].offsetWidth;
    }

    getVisibleCount() {
      const step = this.getCardStep();
      return step ? Math.max(1, Math.round(this.track.clientWidth / step)) : 1;
    }

    getMaxIndex() {
      return Math.max(0, (this.cards?.length || 0) - this.getVisibleCount());
    }

    goTo(index, wrap = false) {
      if (!this.cards?.length) return;
      const maxIndex = this.getMaxIndex();
      let target = index;
      if (wrap && target > maxIndex) target = 0;
      if (wrap && target < 0) target = maxIndex;
      target = this.clamp(target, 0, maxIndex);
      this.currentIndex = target;
      this.track.scrollTo({ left: this.cards[target]?.offsetLeft || 0, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
      this.syncCarouselState();
    }

    syncCarouselState() {
      if (!this.cards?.length) return;
      const step = this.getCardStep();
      this.currentIndex = step ? this.clamp(Math.round(this.track.scrollLeft / step), 0, this.getMaxIndex()) : 0;
      const visibleCount = this.getVisibleCount();
      const lastVisible = Math.min(this.cards.length, this.currentIndex + visibleCount);
      this.carouselLabel.textContent = visibleCount > 1
        ? `Showing ${this.currentIndex + 1}–${lastVisible} of ${this.cards.length} reviews`
        : `Review ${this.currentIndex + 1} of ${this.cards.length}`;
      this.previousButton.disabled = this.currentIndex <= 0;
      this.nextButton.disabled = this.currentIndex >= this.getMaxIndex();
    }

    startDrag(event) {
      if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('a, button')) return;
      this.stopAutoplay();
      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.track.scrollLeft,
        didMove: false
      };
      this.track.setPointerCapture?.(event.pointerId);
      this.track.classList.add('is-dragging');
    }

    moveDrag(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
      const distance = event.clientX - this.dragState.startX;
      if (Math.abs(distance) > 4) this.dragState.didMove = true;
      this.track.scrollLeft = this.dragState.startScrollLeft - distance;
      event.preventDefault();
    }

    endDrag(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
      this.track.releasePointerCapture?.(event.pointerId);
      this.track.classList.remove('is-dragging');
      const didMove = this.dragState.didMove;
      window.setTimeout(() => {
        if (this.dragState) this.dragState.didMove = false;
        if (didMove) this.goTo(this.currentIndex);
        this.startAutoplay();
      }, 0);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.reducedMotion() || document.hidden || !this.cards || this.cards.length <= this.getVisibleCount()) return;
      const interval = Math.max(4000, Number.parseInt(this.dataset.autoplayInterval, 10) || 6000);
      this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1, true), interval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    safeUrl(value, fallback = '') {
      if (!value) return fallback;
      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : fallback;
      } catch (_error) {
        return fallback;
      }
    }

    buildEndpointUrl(value, addRefresh = true) {
      try {
        const url = new URL(value, window.location.origin);
        const localHosts = new Set(['localhost', '127.0.0.1', '[::1]']);
        const isLocalDevelopment = localHosts.has(url.hostname) && ['http:', 'https:'].includes(url.protocol);
        if (url.protocol !== 'https:' && !isLocalDevelopment) return '';
        if (addRefresh) url.searchParams.set('_epelay_refresh', Date.now().toString());
        return url.href;
      } catch (_error) {
        return '';
      }
    }

    createReaderSource(value, label) {
      const readerUrl = this.buildReaderUrl(value);
      if (!readerUrl) return null;

      const parsed = new URL(readerUrl);
      const productId = parsed.pathname.split('/').filter(Boolean).pop() || '';
      return {
        readerUrl,
        productId,
        label: typeof label === 'string' ? label.trim().slice(0, 50) : '',
        sourceUrl: `https://${parsed.pathname.replace(/^\//, '')}`
      };
    }

    buildReaderUrl(value) {
      try {
        const url = new URL(value);
        const expectedProductPath = `/shop.tiktok.com/us/pdp/`;
        if (
          url.protocol !== 'https:' ||
          url.hostname !== 'r.jina.ai' ||
          !url.pathname.startsWith(expectedProductPath) ||
          !/^\d{16,22}$/.test(url.pathname.split('/').filter(Boolean).pop() || '')
        ) {
          return '';
        }
        return url.href;
      } catch (_error) {
        return '';
      }
    }

    firstText(...values) {
      const value = values.find((item) => typeof item === 'string' && item.trim());
      return value ? value.trim() : '';
    }

    firstNumber(...values) {
      for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const number = Number(value);
        if (Number.isFinite(number)) return number;
      }
      return null;
    }

    firstVisibleCharacter(value = '') {
      return Array.from(value.trim())[0] || '';
    }

    formatNumber(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US').format(value);
    }

    formatDate(value) {
      if (!value || !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return value || '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      }).format(date);
    }

    epochToIso(value) {
      const raw = Number(value);
      if (!Number.isFinite(raw) || raw <= 0) return '';
      const date = new Date(raw < 10000000000 ? raw * 1000 : raw);
      return Number.isNaN(date.getTime()) ? '' : date.toISOString();
    }

    countryName(value) {
      const code = typeof value === 'string' ? value.trim().toUpperCase() : '';
      return code === 'US' ? 'United States' : code;
    }

    isObject(value) {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    }

    clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, value));
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('epelay-review-carousel', EpelayReviewCarousel);
})();
